import React, { useEffect, useMemo, useRef, useState } from "react";
import TextFormatPositionPanel from "../../../../components/shared/textToolbar/TextFormatPositionPanel";
import {
  textFormatPatchToWatermark,
  watermarkToTextFormat,
} from "../Editor/watermarkTextFormatAdapter";
import {
  WATERMARK_CONTENT_TYPES,
  applyMarginAlongNearestEdge,
  getWatermarkFontStack,
  markEdgeGapsPx,
  marginFromNearestEdgeGap,
  measureWatermarkText,
  resolveMarginGapPx,
  resolveSingleMarkPosition,
  watermarkFontSizePx,
  watermarkLogoDisplaySize,
} from "./watermarkSettings";
import "./WatermarkControls.css";
import "./WatermarkPositionOverlayContent.css";

function readStageSize(stageSelector) {
  const el = stageSelector ? document.querySelector(stageSelector) : null;
  if (!el) return { width: 0, height: 0 };
  return { width: el.offsetWidth || 0, height: el.offsetHeight || 0 };
}

function resolveMarkMetrics(settings, stageW, stageH) {
  const isLogo =
    settings?.contentType === WATERMARK_CONTENT_TYPES.LOGO &&
    Boolean(settings?.logoUrl);
  const sizeInternal = Number(settings?.size) || 0;
  if (isLogo) {
    return watermarkLogoDisplaySize(
      sizeInternal,
      stageW,
      stageH,
      Number(settings?.logoNaturalWidth) || 0,
      Number(settings?.logoNaturalHeight) || 0
    );
  }
  return measureWatermarkText(
    settings?.text || "Your text",
    watermarkFontSizePx(sizeInternal, stageW, stageH),
    getWatermarkFontStack(settings?.fontId),
    settings?.fontWeight,
    {
      letterSpacing: settings?.letterSpacing,
      textScript: settings?.textScript,
    }
  );
}

/**
 * Watermark host for the shared Position panel.
 * Tile mode stays in left controls; this panel only enables/disables fields.
 */
const WatermarkPositionOverlayContent = ({
  settings,
  onChange,
  onClose = () => {},
  disabled = false,
  positionStageSelector = ".transform-preview__canvas",
}) => {
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const positionValue = useMemo(() => watermarkToTextFormat(settings), [settings]);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });

  const isSingleTile = (settings?.tile || "single") === "single";
  const enablePlacement = isSingleTile;
  const enableSpacing = !isSingleTile;
  const tileLabel =
    settings?.tile === "grid"
      ? "Grid"
      : settings?.tile === "diamond"
        ? "Diamond"
        : "Single";
  const disabledTips = useMemo(
    () => ({
      placement: `Text Position is only available in Single tile (currently ${tileLabel}). Switch Tile to Single.`,
      xy: `X and Y are only available in Single tile (currently ${tileLabel}). Drag the pattern on the canvas to pan, or switch Tile to Single.`,
      spacing: `Spacing is only available in Grid or Diamond tile (currently ${tileLabel}). Switch Tile to Grid or Diamond.`,
    }),
    [tileLabel]
  );

  useEffect(() => {
    const update = () => setStageSize(readStageSize(positionStageSelector));
    update();
    window.addEventListener("resize", update);
    const el = positionStageSelector
      ? document.querySelector(positionStageSelector)
      : null;
    let ro = null;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener("resize", update);
      ro?.disconnect();
    };
  }, [positionStageSelector]);

  const stageW = stageSize.width || 400;
  const stageH = stageSize.height || 400;

  const nearestMarginPct = useMemo(() => {
    if (!isSingleTile) {
      return Math.round(
        Math.max(0, Math.min(0.4, Number(settings?.margin) || 0)) * 100
      );
    }
    const metrics = resolveMarkMetrics(settings, stageW, stageH);
    const base = resolveSingleMarkPosition({
      position: settings?.position || "center",
      margin: settings?.margin,
      stageWidth: stageW,
      stageHeight: stageH,
      textWidth: metrics.width,
      textHeight: metrics.height,
      rotationDeg: Number(settings?.rotation) || 0,
      flushEdges: settings?.flushEdges !== false,
    });
    const centerX = base.x + (Number(settings?.offsetX) || 0);
    const centerY = base.y + (Number(settings?.offsetY) || 0);
    const gaps = markEdgeGapsPx({
      centerX,
      centerY,
      stageWidth: stageW,
      stageHeight: stageH,
      textWidth: metrics.width,
      textHeight: metrics.height,
      rotationDeg: Number(settings?.rotation) || 0,
    });
    const gapPx = resolveMarginGapPx(gaps, settings?.position || "center");
    return Math.round(
      marginFromNearestEdgeGap(gapPx, stageW, stageH) * 100
    );
  }, [settings, stageW, stageH, isSingleTile]);

  const handlePositionChange = (next) => {
    const base = settingsRef.current;
    const { _meta, textCase: _textCase, text: _text, ...incoming } = next;
    const current = watermarkToTextFormat(base);
    const positionPatch = {};
    for (const [key, val] of Object.entries(incoming)) {
      if (current[key] !== val) positionPatch[key] = val;
    }
    // Prefer host margin handler for single (nearest-edge) — skip raw margin here.
    if (
      positionPatch.margin !== undefined &&
      (base.tile || "single") === "single" &&
      positionPatch.offsetX === undefined &&
      positionPatch.offsetY === undefined
    ) {
      delete positionPatch.margin;
    }
    if (Object.keys(positionPatch).length === 0) return;
    onChange(textFormatPatchToWatermark(positionPatch, base));
  };

  const handleMarginChange = (pct) => {
    const live = settingsRef.current;
    const single = (live?.tile || "single") === "single";
    if (!single) {
      onChange({
        ...live,
        margin: Math.max(0, Math.min(0.4, pct / 100)),
        offsetX: 0,
        offsetY: 0,
      });
      return;
    }
    const stage = readStageSize(positionStageSelector);
    const w = stage.width || stageW;
    const h = stage.height || stageH;
    const metrics = resolveMarkMetrics(live, w, h);
    const base = resolveSingleMarkPosition({
      position: live?.position || "center",
      margin: live?.margin,
      stageWidth: w,
      stageHeight: h,
      textWidth: metrics.width,
      textHeight: metrics.height,
      rotationDeg: Number(live?.rotation) || 0,
      flushEdges: live?.flushEdges !== false,
    });
    const applied = applyMarginAlongNearestEdge({
      centerX: base.x + (Number(live?.offsetX) || 0),
      centerY: base.y + (Number(live?.offsetY) || 0),
      position: live?.position || "center",
      nextMargin: Math.max(0, Math.min(0.4, pct / 100)),
      stageWidth: w,
      stageHeight: h,
      textWidth: metrics.width,
      textHeight: metrics.height,
      rotationDeg: Number(live?.rotation) || 0,
      flushEdges: live?.flushEdges !== false,
    });
    onChange({
      ...live,
      margin: applied.margin,
      offsetX: applied.offsetX,
      offsetY: applied.offsetY,
    });
  };

  return (
    <div className="wlp-position-content wmc">
      <TextFormatPositionPanel
        className="tfp-panel--overlay"
        value={positionValue}
        onChange={handlePositionChange}
        disabled={disabled}
        stageSelector={positionStageSelector}
        positionSectionTitle="Text Position"
        enablePlacement={enablePlacement}
        enableSpacing={enableSpacing}
        showSpacing
        showMargin
        marginPercent={nearestMarginPct}
        onMarginPercentChange={handleMarginChange}
        disabledTips={disabledTips}
        sizeConfig={{
          formatDisplay: (v) => String(Math.round(v * 10)),
          parseDisplay: (display) => Number(display) / 10,
        }}
      />
      <div className="wlp-position-content__footer">
        <button
          type="button"
          className="wlp-position-content__close"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default WatermarkPositionOverlayContent;
